// Support API. Ticket servisleri (6500/6501/6502) request.tsx -> support.mobiapi.de'ye gider.
// Eski support.mobiapi.net REST katmani kaldirildi (AG CAGRISI YAPILMIYOR).
import type { Role, Permissions } from '../types/permissions';
import { getTicketsRequest, getTicketRequest, upsertTicketRequest } from './request';
import { getAppToken } from './session';
import { mockGetConversations, mockGetMessages, mockSendMessage } from './chat-mock';

export type { Role };

// Yalnizca socket.io baglantisi icin tutuluyor (su an socket.ts'de devre disi).
export const API_BASE =
  process.env.REACT_APP_API_BASE || 'https://support.mobiapi.net';

// ─── HTTP helpers ────────────────────────────────────────────────────────────
// NOT: support.mobiapi.net REST katmani kaldirildi — bu yardimcilar artik AG CAGRISI YAPMAZ.
// Gercek backend ticket servisleri (6500/6501/6502) request.tsx -> support.mobiapi.de uzerinden gider.
// Henuz .de servisi olmayan ozellikler (users/roles/products/canned/surveys/chat) bos/no-op doner;
// ilgili .de servisi eklenince asagidaki get/post/patch/del/httpList gercege baglanir.

async function http<T>(_path: string, _init?: RequestInit): Promise<T> {
  return undefined as T;
}

async function httpList<T>(_path: string, _query?: Record<string, string | number | boolean | undefined>): Promise<T[]> {
  return [];
}

function get<T>(path: string) {
  return http<T>(path, { method: 'GET' });
}
function post<T>(path: string, body: unknown) {
  return http<T>(path, { method: 'POST', body: JSON.stringify(body) });
}
function patch<T>(path: string, body: unknown) {
  return http<T>(path, { method: 'PATCH', body: JSON.stringify(body) });
}
function del<T>(path: string) {
  return http<T>(path, { method: 'DELETE' });
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SupportUser {
  ref: string;
  full_name: string;
  email: string;
  avatar_url?: string;
  role_id: number;
  role?: { name: string; display_name: string; permissions: Permissions };
  is_active: boolean;
  created_at: string;
}

export interface Product {
  ref: number;
  code: number;
  name: string;
  description?: string;
  icon?: string;
  is_active: boolean;
}

// Vendor = musteri firma/organizasyon (Ticket.vendor_ref bunu isaret eder).
// Alanlar Fores "Vendor Management" ekranindaki gercek vendor kaydiyla eslesir.
export interface Vendor {
  ref: number;             // ID (orn. 10007001)
  name: string;            // Vendor Name
  token?: string;          // Vendor Token (gizli)
  api_url?: string;        // API URL
  image_url?: string;      // Image URL
  email?: string;
  phone?: string;
  contact_person?: string; // Contact (yetkili kisi)
  city?: string;
  address?: string;
  firm_nr?: string;        // Firm Nr
  vat_nr?: string;         // VAT Nr
  country?: string;
  postal_code?: string;    // Postal Code
  comment?: string;
  is_active?: boolean;
  // Destek baglaminda turetilir (vendor kaydinda yoktur), varsa gosterilir:
  open_tickets?: number;
  total_tickets?: number;
  created_at?: string;
}

export interface ProductAssignment {
  ref: number;
  support_user_ref: string;
  product_ref: number;
  is_primary: boolean;
  support_user?: SupportUser;
  product?: Product;
}

export interface CannedResponse {
  ref: string;
  title: string;
  content: string;
  category?: string;
  product_ref?: number;
  is_active: boolean;
  created_by?: string;
  created_at: string;
}

export interface TicketMessage {
  ref: string;
  ticket_ref: string;
  content: string;
  sender_id: string;
  sender_name: string;
  sender_type?: string;
  is_internal?: boolean;
  attachments?: { ref: string; file_name: string; storage_path: string }[];
  created_at: string;
}

// TicketStatus: backend dbo.TicketStatusTypes.StatusNr ile birebir (1..15).
// Uygulama artik kendi numaralandirmasini kullanmiyor; status = ticketStatusNr.
export type TicketStatus = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15;
// TicketPriority (backend schema): 1=high, 2=medium, 3=low
export type TicketPriority = 1 | 2 | 3;

// dbo.TicketStatusTypes ile birebir esleme. Tek dogruluk kaynagi (renk/ceviri buradan turetilir).
export interface TicketStatusDef {
  nr: TicketStatus;
  code: string;      // StatusCode
  labelKey: string;  // i18n anahtari
  isClosed: boolean; // IsClosedStatus
  isActive: boolean; // IsActive (durum secim listesinde gosterilsin mi)
}

export const TICKET_STATUS_DEFS: TicketStatusDef[] = [
  { nr: 1,  code: 'NEW',              labelKey: 'statusNew',             isClosed: false, isActive: true  },
  { nr: 2,  code: 'OPEN',             labelKey: 'statusOpen',            isClosed: false, isActive: true  },
  { nr: 3,  code: 'ASSIGNED',         labelKey: 'statusAssigned',        isClosed: false, isActive: true  },
  { nr: 4,  code: 'IN_PROGRESS',      labelKey: 'statusInProgress',      isClosed: false, isActive: true  },
  { nr: 5,  code: 'WAITING_CUSTOMER', labelKey: 'statusWaitingCustomer', isClosed: false, isActive: true  },
  { nr: 6,  code: 'PENDING',          labelKey: 'statusPending',         isClosed: false, isActive: true  },
  { nr: 7,  code: 'ON_HOLD',          labelKey: 'statusOnHold',          isClosed: false, isActive: true  },
  { nr: 8,  code: 'ESCALATED',        labelKey: 'statusEscalated',       isClosed: false, isActive: true  },
  { nr: 9,  code: 'RESOLVED',         labelKey: 'statusResolved',        isClosed: true,  isActive: true  },
  { nr: 10, code: 'CLOSED',           labelKey: 'statusClosed',          isClosed: true,  isActive: true  },
  { nr: 11, code: 'REOPENED',         labelKey: 'statusReopened',        isClosed: false, isActive: true  },
  { nr: 12, code: 'CANCELLED',        labelKey: 'statusCancelled',       isClosed: true,  isActive: true  },
  { nr: 13, code: 'DUPLICATE',        labelKey: 'statusDuplicate',       isClosed: true,  isActive: false },
  { nr: 14, code: 'REJECTED',         labelKey: 'statusRejected',        isClosed: true,  isActive: false },
  { nr: 15, code: 'EXPIRED',          labelKey: 'statusExpired',         isClosed: true,  isActive: false },
];

const STATUS_DEF_BY_NR = new Map<number, TicketStatusDef>(TICKET_STATUS_DEFS.map(d => [d.nr, d]));

// Bir durumun "kapali" sayilip sayilmadigi (IsClosedStatus).
export function isClosedStatus(nr: number): boolean {
  return STATUS_DEF_BY_NR.get(nr)?.isClosed ?? false;
}

export interface Ticket {
  ref: string;
  ticket_code: string;
  title: string;
  description?: string;
  status: TicketStatus;
  ticket_type?: number;       // ticketTypeNr: Support=1, Bug=2, Feature=3, Service=4
  priority: TicketPriority;
  product_code: number;
  vendor_ref?: string | number;
  contact_ref?: number;
  contact_channel?: number;   // contactChannelRef (AT_ContactChannels)
  support_group?: number;     // supportGroupRef
  assigned_to?: string;
  assigned_to_name?: string;
  customer_email?: string;
  customer_name?: string;
  type_name?: string;          // currentTypeName
  author_name?: string;        // ticketAutorName
  support_group_name?: string; // supportGroupName
  attachments?: string[];
  due_date?: string;
  first_responded_at?: string;
  closed_at?: string;
  created_at: string;
  updated_at?: string;
}

export interface TicketHistory {
  ref: string;
  ticket_ref: string;
  action: string;
  old_value?: string;
  new_value?: string;
  performed_by?: string;
  performed_by_name?: string;
  created_at: string;
}

export interface SatisfactionSurvey {
  ref: string;
  ticket_ref: string;
  score: number;
  feedback?: string;
  created_at: string;
}

function nowIso(): string {
  return new Date().toISOString();
}

// ─── Support Users ───────────────────────────────────────────────────────────

export async function getSupportUsers(activeOnly = true): Promise<SupportUser[]> {
  const all = await httpList<SupportUser>('/api/support-users');
  return activeOnly ? all.filter(u => u.is_active) : all;
}

export async function createSupportUser(data: {
  email: string;
  password: string;
  full_name: string;
  role_id: number;
}): Promise<void> {
  await post('/api/support-users', data);
}

export async function updateSupportUser(id: string, data: Partial<SupportUser>): Promise<void> {
  await patch(`/api/support-users/${id}`, data);
}

export async function deleteSupportUser(id: string): Promise<void> {
  await del(`/api/support-users/${id}`);
}

// ─── Roles ───────────────────────────────────────────────────────────────────

export async function getRoles(_activeOnly = true): Promise<Role[]> {
  return httpList<Role>('/api/roles');
}

export async function createRole(data: {
  name: string;
  display_name: string;
  description?: string;
  permissions?: Permissions;
  sort_order?: number;
}): Promise<{ isOk: boolean; data?: Role; message?: string }> {
  try {
    const role = await post<Role>('/api/roles', data);
    return { isOk: true, data: role };
  } catch (e: any) {
    return { isOk: false, message: e?.message || 'Hata' };
  }
}

export async function updateRole(
  id: number,
  updates: Partial<Role>,
): Promise<{ isOk: boolean; data?: Role; message?: string }> {
  try {
    const role = await patch<Role>(`/api/roles/${id}`, updates);
    return { isOk: true, data: role };
  } catch (e: any) {
    return { isOk: false, message: e?.message || 'Hata' };
  }
}

export async function deleteRole(id: number): Promise<{ isOk: boolean; message?: string }> {
  try {
    await del(`/api/roles/${id}`);
    return { isOk: true };
  } catch (e: any) {
    return { isOk: false, message: e?.message || 'Hata' };
  }
}

// ─── Products ────────────────────────────────────────────────────────────────

export async function getProducts(activeOnly = true): Promise<Product[]> {
  const all = await httpList<Product>('/api/products');
  return activeOnly ? all.filter(p => p.is_active) : all;
}

export async function createProduct(data: Partial<Omit<Product, 'ref'>>): Promise<void> {
  await post('/api/products', data);
}

export async function updateProduct(id: number, data: Partial<Product>): Promise<void> {
  await patch(`/api/products/${id}`, data);
}

export async function deleteProduct(id: number): Promise<void> {
  await del(`/api/products/${id}`);
}

// ─── Support Groups (destek gruplari) ────────────────────────────────────────
// .de servisi gelene kadar httpList bos doner; UI'da mock fallback kullanilir.
export interface SupportGroup {
  ref: number;
  name: string;
  is_active?: boolean;
}

export async function getSupportGroups(activeOnly = true): Promise<SupportGroup[]> {
  const all = await httpList<SupportGroup>('/api/support-groups');
  return activeOnly ? all.filter(g => g.is_active !== false) : all;
}

// ─── Vendors (musteri firmalar) ──────────────────────────────────────────────
// .de servisi gelene kadar httpList bos doner; UI tarafinda mock fallback kullanilir
// (bkz. pages/support/vendors/vendor-mock.ts).

export async function getVendors(activeOnly = true): Promise<Vendor[]> {
  const all = await httpList<Vendor>('/api/vendors');
  return activeOnly ? all.filter(v => v.is_active !== false) : all;
}

export async function getVendor(ref: number | string): Promise<Vendor | null> {
  const all = await getVendors(false);
  return all.find(v => String(v.ref) === String(ref)) ?? null;
}

// ─── Product Assignments ─────────────────────────────────────────────────────

export async function getProductAssignments(): Promise<ProductAssignment[]> {
  return httpList<ProductAssignment>('/api/product-assignments');
}

export async function createProductAssignment(
  data: Omit<ProductAssignment, 'ref' | 'support_user' | 'product'>,
): Promise<void> {
  await post('/api/product-assignments', data);
}

export async function updateProductAssignment(
  id: number,
  data: Partial<ProductAssignment>,
): Promise<void> {
  await patch(`/api/product-assignments/${id}`, data);
}

export async function deleteProductAssignment(id: number): Promise<void> {
  await del(`/api/product-assignments/${id}`);
}

// ─── Canned Responses ────────────────────────────────────────────────────────

export async function getAllCannedResponses(): Promise<CannedResponse[]> {
  return httpList<CannedResponse>('/api/canned-responses');
}

export async function getCannedResponses(): Promise<CannedResponse[]> {
  const all = await getAllCannedResponses();
  return all.filter(r => r.is_active);
}

export async function createCannedResponse(
  data: Partial<Omit<CannedResponse, 'ref'>> & { title: string; content: string },
): Promise<void> {
  await post('/api/canned-responses', data);
}

export async function updateCannedResponse(id: string, data: Partial<CannedResponse>): Promise<void> {
  await patch(`/api/canned-responses/${id}`, data);
}

export async function deleteCannedResponse(id: string): Promise<void> {
  await del(`/api/canned-responses/${id}`);
}

// ─── Tickets ─────────────────────────────────────────────────────────────────

export async function createTicket(
  data: Omit<Ticket, 'ref' | 'ticket_code' | 'created_at' | 'updated_at'>,
): Promise<Ticket> {
  const token = getAppToken();
  const res: any = await upsertTicketRequest(
    {
      ticketRef: 0, // insert
      ticketStatusNr: appStatusToBackend(data.status ?? 1),
      ticketTypeNr: Number(data.ticket_type ?? 1), // varsayilan: Support Request
      ticketTitle: data.title,
      ticketDescription: data.description ?? '',
      vendorRef: Number(data.vendor_ref ?? 0),
      contactRef: Number(data.contact_ref ?? 0),
      contactChannelRef: Number(data.contact_channel ?? 1),
      supportGroupRef: Number(data.support_group ?? 0),
      supportProductRef: Number(data.product_code ?? 0),
      ticketPriorityNr: appPriorityToBackend(data.priority ?? 2),
      assignedToRef: Number(data.assigned_to ?? 0),
    },
    token,
  );
  const created = Array.isArray(res?.dataSet) ? res.dataSet[0] : res;
  const newRefVal = created?.ticketRef ?? res?.ticketRef;
  return {
    ref: String(newRefVal ?? ''),
    ticket_code: created?.ticketCode ?? '',
    created_at: created?.createDate ?? nowIso(),
    ...data,
  };
}

export interface TicketListParams {
  date_from?: string;  // ISO (YYYY-MM-DD veya tam ISO)
  date_to?: string;
  status?: number | string;        // ticketStatusNr (dbo.TicketStatusTypes)
  ticket_type?: number | string;   // ticketTypeNr (Support=1, Bug=2, Feature=3, Service=4)
  priority?: number | string;
  product_code?: number;
  assigned_to?: string;            // assignedToRef. "0" = atanmamis Ticketler
}

// 6500 servisi durum/onceligi isim olarak donduruyor ( or. "New", "Very High").
// Bunlari Ticket semasindaki sayisal degerlere cevirmek icin esleme tablolari.
function normalizeName(s: unknown): string {
  return String(s ?? '').toLowerCase().replace(/[\s_-]/g, '');
}

// StatusCode ve StatusName normalize edilince ayni stringe duser (NEW/"New",
// IN_PROGRESS/"In Progress" vb.), bu yuzden tabloyu tek kaynaktan turetiyoruz.
const STATUS_NAME_TO_NUM: Record<string, TicketStatus> = Object.fromEntries(
  TICKET_STATUS_DEFS.map(d => [normalizeName(d.code), d.nr]),
) as Record<string, TicketStatus>;

const PRIORITY_NAME_TO_NUM: Record<string, TicketPriority> = {
  veryhigh: 1, high: 1, urgent: 1, critical: 1,
  medium: 2, normal: 2,
  low: 3, verylow: 3,
};

function pickStatus(r: any): TicketStatus {
  const numeric = r.status ?? r.ticketStatus ?? r.TicketStatus ?? r.ticketStatusNr;
  if (numeric !== undefined && numeric !== null && numeric !== '') {
    return Number(numeric) as TicketStatus;
  }
  const name = r.currentStatusName ?? r.statusName ?? r.CurrentStatusName;
  return name ? STATUS_NAME_TO_NUM[normalizeName(name)] ?? 1 : 1;
}

function pickPriority(r: any): TicketPriority {
  const numeric = r.priority ?? r.ticketPriority ?? r.TicketPriority ?? r.ticketPriorityNr;
  if (numeric !== undefined && numeric !== null && numeric !== '') {
    return Number(numeric) as TicketPriority;
  }
  const name = r.currentPriorityName ?? r.priorityName ?? r.CurrentPriorityName;
  return name ? PRIORITY_NAME_TO_NUM[normalizeName(name)] ?? 2 : 2;
}

function mapTicketFromApi(r: any): Ticket {
  return {
    ref: String(r.ref ?? r.ticketRef ?? r.ticket_ref ?? r.TicketRef ?? r.id ?? ''),
    ticket_code: r.ticket_code ?? r.ticketCode ?? r.TicketCode ?? r.code ?? '',
    title: r.title ?? r.subject ?? r.ticketTitle ?? r.TicketTitle ?? r.Title ?? '',
    description: r.description ?? r.ticketDescription ?? r.TicketDescription ?? r.body ?? '',
    status: pickStatus(r),
    ticket_type: Number(r.ticket_type ?? r.ticketTypeNr ?? r.currentTypeNr ?? r.TicketTypeNr ?? 0) || undefined,
    priority: pickPriority(r),
    product_code: Number(r.product_code ?? r.productCode ?? r.ProductCode ?? 0),
    vendor_ref: r.vendor_ref ?? r.vendorRef ?? r.VendorRef ?? r.vendorId ?? r.VendorId,
    contact_ref: r.contact_ref ?? r.contactRef ?? r.ContactRef,
    contact_channel: Number(r.contact_channel ?? r.contactChannelRef ?? r.ContactChannelRef ?? 0) || undefined,
    assigned_to: r.assigned_to ?? r.assignedTo ?? r.AssignedTo ?? r.assignedUserRef,
    assigned_to_name: r.assigned_to_name ?? r.assignedToName ?? r.AssignedToName ?? r.assignedUserName,
    customer_email: r.customer_email ?? r.customerEmail ?? r.CustomerEmail ?? r.contactEmail ?? r.ContactEmail,
    customer_name: r.customer_name ?? r.customerName ?? r.CustomerName ?? r.companyName ?? r.contactName ?? r.ContactName,
    type_name: r.type_name ?? r.currentTypeName ?? r.CurrentTypeName ?? r.ticketTypeName ?? '',
    author_name: r.author_name ?? r.ticketAutorName ?? r.TicketAutorName ?? r.ticketAuthorName ?? '',
    support_group_name: r.support_group_name ?? r.supportGroupName ?? r.SupportGroupName ?? '',
    due_date: r.due_date ?? r.dueDate ?? r.DueDate ?? r.resolutionDueDate ?? r.ResolutionDueDate,
    first_responded_at: r.first_responded_at ?? r.firstRespondedAt ?? r.FirstRespondedAt,
    closed_at: r.closed_at ?? r.closedAt ?? r.ClosedAt,
    created_at: r.created_at ?? r.createdAt ?? r.CreatedAt ?? r.createDate ?? r.CreateDate ?? new Date().toISOString(),
    updated_at: r.updated_at ?? r.updatedAt ?? r.UpdatedAt,
  };
}

// status alani artik backend ticketStatusNr (dbo.TicketStatusTypes) ile birebir;
// ek bir cevirim gerekmiyor.
function appStatusToBackend(s: TicketStatus): number {
  return Number(s);
}
// Uygulama oncelik (1=high,2=medium,3=low). Backend ticketPriorityNr: 1="Very High" dogrulandi.
// Diger seviyeler backend ile teyit edilmeli; simdilik 1:1 esleniyor.
function appPriorityToBackend(p: TicketPriority): number {
  return Number(p);
}

// 6502 tam upsert oldugu icin once 6501 ile ham kaydi cekip, sadece degisen alani override ederiz.
async function upsertTicketChange(
  id: string,
  overrides: Partial<{
    ticketStatusNr: number; ticketTypeNr: number; ticketTitle: string;
    ticketDescription: string; ticketPriorityNr: number; assignedToRef: number;
    supportGroupRef: number; supportProductRef: number; contactChannelRef: number;
  }>,
): Promise<void> {
  const token = getAppToken();
  const detail = await getTicketRequest(Number(id), token);
  const r: any = Array.isArray(detail?.dataSet)
    ? detail.dataSet[0]
    : Array.isArray(detail)
    ? detail[0]
    : detail;
  if (!r) throw new Error('ticket not found');
  await upsertTicketRequest(
    {
      ticketRef: Number(r.ticketRef ?? id),
      ticketStatusNr: r.currentStatusNr ?? -1,
      ticketTypeNr: r.currentTypeNr ?? -1,
      ticketTitle: r.ticketTitle ?? '',
      ticketDescription: r.ticketDescription ?? '',
      vendorRef: r.vendorRef ?? 0,
      contactRef: r.contactRef ?? 0,
      contactChannelRef: r.contactChannelRef ?? 0,
      supportGroupRef: r.supportGroupRef ?? 0,
      supportProductRef: r.supportProductRef ?? 0,
      ticketPriorityNr: r.currentPriorityNr ?? -1,
      assignedToRef: r.assignedToRef ?? 0,
      ...overrides,
    },
    token,
  );
}

export async function getTickets(params?: TicketListParams): Promise<Ticket[]> {

  const token=getAppToken()
  // -1 = filtre yok (hepsi). Tum filtreler backend'e (6500 / support.mobiapi.de) gonderilir.
  const statusFilter =
    params?.status !== undefined && params.status !== ''
      ? appStatusToBackend(Number(params.status) as TicketStatus)
      : -1;
  const typeFilter =
    params?.ticket_type !== undefined && params.ticket_type !== ''
      ? Number(params.ticket_type)
      : -1;
  const priorityFilter =
    params?.priority !== undefined && params.priority !== ''
      ? appPriorityToBackend(Number(params.priority) as TicketPriority)
      : -1;
  // assigned_to "0" => atanmamis Ticketler (assignedToRef=0); bos/undefined => filtre yok (-1).
  const assignedFilter =
    params?.assigned_to !== undefined && params.assigned_to !== ''
      ? Number(params.assigned_to)
      : -1;
  const data = await getTicketsRequest(token, {
    ticketStatusNr: statusFilter,
    ticketTypeNr: typeFilter,
    ticketPriorityNr: priorityFilter,
    assignedToRef: assignedFilter,
  });
  const raw: any[] = Array.isArray(data)
    ? data
    : Array.isArray(data?.dataSet)
    ? data.dataSet
    : Array.isArray(data?.records)
    ? data.records
    : Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.tickets)
    ? data.tickets
    : [];

  let list = raw.map(mapTicketFromApi);

  if (params?.priority !== undefined && params.priority !== '') {
    list = list.filter(t => String(t.priority) === String(params.priority));
  }
  if (params?.product_code !== undefined) {
    list = list.filter(t => t.product_code === Number(params.product_code));
  }
  // assigned_to / ticket_type / status backend'de (6500) filtreleniyor; burada tekrar daraltmiyoruz.
  if (params?.date_from) {
    const from = new Date(params.date_from).getTime();
    list = list.filter(t => new Date(t.created_at).getTime() >= from);
  }
  if (params?.date_to) {
    const to = new Date(params.date_to).getTime();
    list = list.filter(t => new Date(t.created_at).getTime() <= to);
  }

  return list.sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}

export async function getTicketStats(): Promise<{
  total: number;
  byStatus: Record<number, number>;
  byPriority: Record<number, number>;
  byProduct: Record<number, number>;
}> {
  const tickets = await getTickets();
  const byStatus: Record<number, number> = {};
  const byPriority: Record<number, number> = {};
  const byProduct: Record<number, number> = {};
  for (const t of tickets) {
    byStatus[t.status] = (byStatus[t.status] ?? 0) + 1;
    byPriority[t.priority] = (byPriority[t.priority] ?? 0) + 1;
    byProduct[t.product_code] = (byProduct[t.product_code] ?? 0) + 1;
  }
  return { total: tickets.length, byStatus, byPriority, byProduct };
}

export async function getTicket(id: string): Promise<Ticket | null> {
  try {
    const token = getAppToken();
    const data = await getTicketRequest(Number(id), token);
    const raw: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.dataSet)
      ? data.dataSet
      : Array.isArray(data?.records)
      ? data.records
      : Array.isArray(data?.data)
      ? data.data
      : [];
    return raw.length ? mapTicketFromApi(raw[0]) : null;
  } catch {
    return null;
  }
}

export async function getTicketMessages(ticketId: string): Promise<TicketMessage[]> {
  const msgs = await httpList<TicketMessage>('/api/ticket-messages', { ticket_ref: ticketId });
  return msgs.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}

export async function getTicketHistory(ticketId: string): Promise<TicketHistory[]> {
  const hist = await httpList<TicketHistory>('/api/ticket-history', { ticket_ref: ticketId });
  return hist.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}

export async function updateTicket(id: string, data: Partial<Ticket>): Promise<void> {
  const overrides: Parameters<typeof upsertTicketChange>[1] = {};
  if (data.status !== undefined) overrides.ticketStatusNr = appStatusToBackend(data.status);
  if (data.priority !== undefined) overrides.ticketPriorityNr = appPriorityToBackend(data.priority);
  if (data.title !== undefined) overrides.ticketTitle = data.title;
  if (data.description !== undefined) overrides.ticketDescription = data.description;
  if (data.assigned_to !== undefined) overrides.assignedToRef = Number(data.assigned_to);
  if (data.product_code !== undefined) overrides.supportProductRef = Number(data.product_code);
  if (data.ticket_type !== undefined) overrides.ticketTypeNr = Number(data.ticket_type);
  await upsertTicketChange(id, overrides);
}

export async function updateTicketStatus(id: string, status: string | number): Promise<void> {
  await upsertTicketChange(id, { ticketStatusNr: appStatusToBackend(Number(status) as TicketStatus) });
}

export async function assignTicket(
  id: string,
  assigneeId: string,
  assignedBy: string,
): Promise<void> {
  await upsertTicketChange(id, { assignedToRef: Number(assigneeId) });
}

export async function closeTicket(id: string, closedBy: string): Promise<void> {
  await post(`/api/tickets/${id}/close`, { closed_by: closedBy });
}

export async function escalateTicket(id: string, reason: string, escalatedBy: string): Promise<void> {
  await post(`/api/tickets/${id}/escalate`, { reason, escalated_by: escalatedBy });
}

export async function sendMessage(
  ticketId: string,
  content: string,
  senderId: string,
  senderName: string,
  isInternal?: boolean,
): Promise<TicketMessage> {
  return post<TicketMessage>('/api/ticket-messages', {
    ticket_ref: ticketId,
    content,
    sender_id: senderId,
    sender_name: senderName,
    sender_type: 'support',
    is_internal: !!isInternal,
    attachments: [],
  });
}

export async function uploadAttachment(
  _ticketId: string,
  _messageId: string,
  _file: File,
  _userId: string,
): Promise<void> {
  // TODO: support.mobiapi.de attachment servisi eklenince baglanacak (no-op).
}

export async function createTicketHistory(
  data: Partial<Omit<TicketHistory, 'ref'>> & { ticket_ref: string },
): Promise<void> {
  await post('/api/ticket-history', data);
}

export function getAttachmentUrl(path: string): string {
  if (!path) return '';
  // .net REST kaldirildi; sadece tam URL'leri oldugu gibi don, digerlerini path olarak birak.
  return path;
}

// ─── Satisfaction Surveys ────────────────────────────────────────────────────

export async function getSurveyByTicket(ticketId: string): Promise<SatisfactionSurvey | null> {
  const list = await httpList<SatisfactionSurvey>('/api/satisfaction-surveys', { ticket_ref: ticketId });
  return list[0] ?? null;
}

export async function submitSurvey(
  ticketId: string,
  score: number,
  feedback?: string,
): Promise<SatisfactionSurvey> {
  return post<SatisfactionSurvey>('/api/satisfaction-surveys', {
    ticket_ref: ticketId,
    score,
    feedback,
  });
}

export interface SurveyWithTicket extends SatisfactionSurvey {
  ticket_code: string;
  ticket_title: string;
  ticket_description?: string;
  ticket_status: number;
  ticket_priority: number;
  customer_name?: string;
  customer_email?: string;
  product_code: number;
  assigned_to_name?: string;
  closed_at?: string;
  ticket_created_at?: string;
}

export async function getAllSurveys(): Promise<SurveyWithTicket[]> {
  const surveys = await httpList<SatisfactionSurvey>('/api/satisfaction-surveys');
  const tickets = await getTickets();
  const byRef = new Map(tickets.map(t => [t.ref, t]));
  return surveys
    .map(s => {
      const ticket = byRef.get(s.ticket_ref);
      return {
        ...s,
        ticket_code: ticket?.ticket_code ?? '',
        ticket_title: ticket?.title ?? '',
        ticket_description: ticket?.description,
        ticket_status: ticket?.status ?? 1,
        ticket_priority: ticket?.priority ?? 0,
        customer_name: ticket?.customer_name,
        customer_email: ticket?.customer_email,
        product_code: ticket?.product_code ?? 0,
        assigned_to_name: ticket?.assigned_to_name,
        closed_at: ticket?.closed_at,
        ticket_created_at: ticket?.created_at,
      };
    })
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
}

export async function getSurveyStats(): Promise<{
  total: number;
  responded: number;
  avgScore: number;
  distribution: Record<number, number>;
}> {
  const surveys = await httpList<SatisfactionSurvey>('/api/satisfaction-surveys');
  const tickets = await getTickets();
  const total = tickets.filter(t => isClosedStatus(t.status)).length;
  const responded = surveys.length;
  const avgScore =
    responded > 0
      ? Math.round((surveys.reduce((sum, s) => sum + s.score, 0) / responded) * 10) / 10
      : 0;
  const distribution: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  surveys.forEach(s => {
    distribution[s.score] = (distribution[s.score] ?? 0) + 1;
  });
  return { total, responded, avgScore, distribution };
}

// ─── Team Chat ───────────────────────────────────────────────────────────────

export interface ChatMessage {
  ref: string;
  conversation_ref: string;
  content: string;
  sender_id: string;
  sender_name: string;
  created_at: string;
}

export interface Conversation {
  ref: string;
  participants: string[];
  participant_names: string[];
  last_message?: string;
  last_message_at?: string;
  unread_count?: number;
  type?: 'personal' | 'group' | 'company'; // mesajlasma turu
  name?: string;                            // grup/firma sohbetlerinde ad
}

export async function getConversations(userId: string): Promise<Conversation[]> {
  const all = await httpList<Conversation>('/api/conversations');
  // Backend bos donerse mock store (chat-mock) kullanilir.
  if (!all.length) return mockGetConversations(userId);
  return all
    .filter(c => c.participants?.includes(userId))
    .sort(
      (a, b) =>
        new Date(b.last_message_at || 0).getTime() - new Date(a.last_message_at || 0).getTime(),
    );
}

export async function getChatMessages(conversationId: string): Promise<ChatMessage[]> {
  const msgs = await httpList<ChatMessage>('/api/chat-messages', { conversation_ref: conversationId });
  if (!msgs.length) return mockGetMessages(conversationId);
  return msgs.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}

export async function sendChatMessage(
  conversationId: string,
  content: string,
  senderId: string,
  senderName: string,
): Promise<ChatMessage> {
  const res = await post<ChatMessage>('/api/chat-messages', {
    conversation_ref: conversationId,
    content,
    sender_id: senderId,
    sender_name: senderName,
  });
  // Backend no-op ise mock store'a yaz.
  return res ?? mockSendMessage(conversationId, content, senderId, senderName);
}

export async function getOrCreateConversation(
  currentUserId: string,
  targetUserId: string,
): Promise<Conversation> {
  const all = await httpList<Conversation>('/api/conversations');
  const existing = all.find(
    c =>
      c.participants?.length === 2 &&
      c.participants.includes(currentUserId) &&
      c.participants.includes(targetUserId),
  );
  if (existing) return existing;

  const users = await getSupportUsers(false);
  const current = users.find(u => u.ref === currentUserId);
  const target = users.find(u => u.ref === targetUserId);
  return post<Conversation>('/api/conversations', {
    participants: [currentUserId, targetUserId],
    participant_names: [current?.full_name || currentUserId, target?.full_name || targetUserId],
    last_message: '',
    unread_count: 0,
  });
}
